import { bindAll, nonenumerable } from 'shared/decorators';
import { injectable } from 'shared/stores/core/decorators';
import { IA_JOB_STATUS } from 'lbs-pi-platform-access/es/data-server/image-analysis/jobs/consts';
import { createLogger } from 'shared/services/logger';

const logger = createLogger('IA:Batch');

/**
 * - Manages a batch of regions to be analyzed
 *   1. Submits a job
 *   2. Polls the job for completion (using jobmonitor)
 *   3. Allows job to be cancelled
 * - Exposes state of the submitted job
 */

export const IA_BATCH_STATUS = {
  UNKNOWN: -100,
  NOT_STARTED: 0,
  STARTING: 1,
  RUNNING: 2,
  PAUSED: 3,
  FINISHED: 4,
  CANCELLING: 11,
  CANCELED: 12,
  FAILED: 21
};

@injectable('IA_JOB_BATCH')
@bindAll
export class Batch {
  state = {
    jobProgress: 0,
    jobStatus: IA_JOB_STATUS.QUEUED,
    status: IA_BATCH_STATUS.NOT_STARTED
  };

  // "Protected"
  @nonenumerable di = null;
  @nonenumerable jobMonitor = null;
  @nonenumerable jobParams = null;

  constructor (di, jobParams) {
    this.di = di;
    this.jobParams = jobParams;
    this.jobMonitor = this.di.factory.create('IA_JOB_MONITOR');
    this.jobMonitor.on('update', this.onJobUpdate.bind(this));
  }

  get isRunning () {
    return this.state.status > IA_BATCH_STATUS.NOT_STARTED && this.state.status < IA_BATCH_STATUS.FINISHED;
  }

  async start () {
    if (this.isRunning) {
      return;
    }
    this.state.status = IA_BATCH_STATUS.STARTING;

    // Create a job
    const jobProvider = this.di.resources.get('IA_JOBS');
    const response = await jobProvider.add(this.jobParams);
    const job = response.data;
    this.jobMonitor.jobId = job.id;
  }

  setJob (job) {
    if (job.status.code >= IA_JOB_STATUS.COMPLETED) {
      logger.warn('Attempted to set finished job');
      return;
    } else if (job.status.code < IA_JOB_STATUS.QUEUED) {
      logger.warn('Attempted to set error\'d job');
      return;
    }

    this.state.status = (job.status.code === IA_JOB_STATUS.QUEUED) ? IA_BATCH_STATUS.STARTING : IA_BATCH_STATUS.RUNNING;
    this.onJobUpdate(job);
    this.jobMonitor.jobId = job.jobQueueId;
  }

  cancel () {
    if (!this.isRunning) {
      logger.warn('Cannot cancel job that is not running.');
      return;
    } else if (this.state.status === IA_BATCH_STATUS.CANCELLING) {
      logger.warn(' Job is already cancelling.');
      return;
    }

    this.state.status = IA_BATCH_STATUS.CANCELLING;

    const jobProvider = this.di.resources.get('IA_JOBS');
    return jobProvider.cancel(this.jobMonitor.jobId);
  }

  async watchJob () {
    // It's possible to pause in between starting a job and watching it; check we're not paused first
    if (this.state.status !== IA_BATCH_STATUS.PAUSED) {
      this.state.status = IA_BATCH_STATUS.RUNNING;

      // Handle errors with this job so the coordinator can kick off the next job.
      try {
        const jobState = await this.jobMonitor.start(); // Should only resolve with FINISHED or CANCELLED
        this.state.status = IA_JOB_STATUS_TO_IA_BATCH_STATUS_MAP[jobState.status.code] || IA_BATCH_STATUS.UNKNOWN;
      } catch (error) {
        logger.error('Job failed', error);
        this.state.status = IA_BATCH_STATUS.FAILED;
        // Notify?
      }
    }
  }

  onJobUpdate (job) {
    this.state.jobStatus = job.status.code;
    this.state.jobProgress = job.status.progress;
  }

  pause () {
    if (this.isRunning) {
      this.state.status = IA_BATCH_STATUS.PAUSED;
      this.jobMonitor.pause();
    }
  }

  async resume () {
    if (this.state.status !== IA_BATCH_STATUS.PAUSED) { // only resume from a pause
      return;
    }
    this.state.status = IA_BATCH_STATUS.RUNNING;
    if (!this.jobMonitor.isStarted) {
      return this.watchJob();
    }
    this.jobMonitor.resume();
  }
}

const IA_JOB_STATUS_TO_IA_BATCH_STATUS_MAP = {
  [IA_JOB_STATUS.CANCELLED]: IA_BATCH_STATUS.CANCELED,
  [IA_JOB_STATUS.COMPLETED]: IA_BATCH_STATUS.FINISHED
};



// WEBPACK FOOTER //
// ./src/shared/stores/image-analysis/jobs/Batch.js